<template>
  <div
    class="overlay"
    v-click-outside="closeOverlay"
  >
    <div class="close-search-overlay">
      <h4>{{ i18n.tv('searchResults', 'searchResults') }}</h4>
      <NavigationClose
        class="close"
        @click="closeOverlay"
      />
    </div>
    <suggestions-grid
      v-if="searchTermResult?.customerSuggestions?.length > 0"
      :suggestions="searchTermResult.customerSuggestions"
      @suggestion-clicked="openBuyBoxSku"
      @buybox-clicked="openBuyBoxSku"
      :show-all-results="true"
      :search-term="searchTerm"
      :search-filter="searchFilter"
      :l2categories="l2categories"
      :availabilities="suggestionAvailabilities"
      :loaded-availability-skus="loadedSuggestionAvailabilitySkus"
    />
    <div
      v-if="searchTermResult && searchTermResult.searchResults && searchTermResult.searchResults.length"
      class="overlay__result"
    >
      <div class="overlay__header">
        <div>
          <span v-tv:searchResults="matchedLabelKey" />
          <span v-text="searchTermResult?.count?.toLocaleString($i18n.locale === 'en' ? 'en' : 'de')" />
        </div>
        <nuxt-link
          @click="closeOverlay"
          :to="resultPathWithCategory"
          class="show-all-results"
        >
          {{ i18n.tv('ShowAllResults','searchResults') }}
          <ArrowForwardIcon class="forward-icon" />
        </nuxt-link>
      </div>
      <NewSearchOverlayResults
        :products="mappedProducts"
        :l2categories="l2categories"
        :categories="categories"
        :search-term="searchTerm"
        :is-any-search="isAnySearch"
        :search-filter="searchFilter"
        :is-inch-search="isInchSearch"
        :availabilities="productAvailabilities"
        :loaded-availability-skus="loadedProductAvailabilitySkus"
        @close-overlay="closeOverlay"
        @openBuyBox="openBuyBox"
        @clickItem="clickItem"
      />
    </div>
    <div
      v-else-if="showNoProductsFound"
      class="overlay__noProducts"
    >
      <div
        class="h5"
        v-tv:searchResults="'NoProducts'"
      />
    </div>
    <div
      v-else-if="searchTermResult && !searchTermResult?.customerSuggestions?.length > 0"
      class="overlay__skeleton"
    >
      <SearchSkeleton
        @close="closeOverlay"
      />
    </div>
  </div>
</template>
<script>
import NewSearchOverlayResults from '~/components/search/NewSearchOverlayResults.vue'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import SearchSkeleton from '~/components/skeletons/SearchSkeleton.vue'
import {getMappedProducts} from '~/assets/js/utils/product_attributes_mapping'
import {storeToRefs} from 'pinia'
import {useUserStore} from '~/store/user.js'
import SuggestionsGrid from './SuggestionsGrid.vue'
import ArrowForwardIcon from '~/assets/icons/md_arrow_forward.svg?component'
import useI18nExtended from '~/composables/usei18nExtended'
import {usePimHelper} from '~/composables/pim_helper'

export default {
  components: {
    NavigationClose,
    NewSearchOverlayResults,
    SearchSkeleton,
    SuggestionsGrid,
    ArrowForwardIcon
  },
  async setup() {
    const {loadedCustomerUuid} = storeToRefs(useUserStore())
    const {loadProductCategoryWithPim} = usePimHelper()

    return {
      currentCustomerUuid: loadedCustomerUuid,
      loadProductCategoryWithPim
    }
  },
  props: {
    searchTermResult: {
      type: Object,
      default () {
        return {}
      }
    },
    l2categories: {
      type: Array,
      default () {
        return []
      }
    },
    categories: {
      type: Array,
      default () {
        return []
      }
    },
    searchTerm: {
      type: String,
      default() {
        return ''
      }
    },
    searchFilter: {
      type: Array,
      default () {
        return []
      }
    },
    isInchSearch: {
      type: Boolean,
      default() {
        return false
      }
    },
    isAnySearch: {
      type: Boolean,
      default() {
        return false
      }
    },
    productAvailabilities: {
      type: Array,
      default() {
        return []
      }
    },
    suggestionAvailabilities: {
      type: Array,
      default() {
        return []
      }
    },
    loadedProductAvailabilitySkus: {
      type: Array,
      default() {
        return []
      }
    },
    loadedSuggestionAvailabilitySkus: {
      type: Array,
      default() {
        return []
      }
    }
  },
  data() {
    return {
      topResults: [],
      i18n: useI18nExtended(),
      categoryJson: null
    }
  },
  methods: {
    closeOverlay(event) {
      const searchElement = document.body.getElementsByClassName('nav__main__searchbar').item(0)
      const target = event?.target
      if (searchElement && target instanceof Node && !searchElement.contains(target)) {
        this.$emit('close-overlay')
      }
    },
    openBuyBox(product) {
      this.$emit('openBuyBox', product)
    },
    openBuyBoxSku({sku, index}) {
      this.$searchInsights?.trackEvent({
        name: 'ClickSuggest', properties: {
          searchServiceName: this.searchTermResult?.azureSearchTelemetry?.searchServiceName,
          searchId: this.searchTermResult?.azureSearchTelemetry?.searchId,
          clickedDocId: sku,
          sku: sku,
          rank: index
        }
      })
      this.$emit('openBuyBoxSku', sku)
    },
    clickItem(sku, rank) {
      this.$emit('clickItem', sku, rank)
      this.$emit('close-overlay')
    },
    async loadCategoryData() {
      if (this.searchFilter && this.searchFilter.length > 0) {
        this.categoryJson = await this.loadProductCategoryWithPim(this.searchFilter, this.$route)
      }
    }
  },
  computed: {
    matchedLabelKey() {
      return this.isAnySearch ? 'anySearchMatches' : 'itemMatches'
    },
    mappedProducts() {
      let mappedProducts = getMappedProducts(this.searchTermResult)
      return mappedProducts?.length > 8 ? mappedProducts.slice(0, 8) : mappedProducts
    },
    showNoProductsFound() {
      return this.searchTermResult
        && this.searchTermResult.searchResults
        && this.searchTermResult.searchResults.length === 0
        && (this.currentCustomerUuid !== 'default' ? this.searchTermResult?.customerSuggestions?.length === 0 : true)
    },
    resultPath() {
      const path = this.localePath({name: 'searchResults'})
      return path + '?query=' + encodeURIComponent(this.searchTerm)
    },
    resultPathWithCategory() {
      let resultingPath = this.resultPath

      if (this.l2categories?.length === 1 &&
          this.searchTermResult?.count === this.l2categories[0]?.hitCount) {
        resultingPath = `${resultingPath}&PrimaryL2Category=${this.l2categories[0].categoryName}`
      }

      if (this.searchFilter.length === 0) {
        return resultingPath
      }
      let catFilter = ''
      if (this.searchFilter && this.categoryJson?.designTechnicalNames?.length > 0) {
        this.categoryJson.designTechnicalNames.forEach(filter => {
          if (catFilter === '') {
            catFilter = `ProductDesign=${filter}`
          } else {
            catFilter += `%24${filter}`
          }
        })
      }
      return `${resultingPath}&${catFilter}`
    }
  },
  watch: {
    searchFilter: {
      handler: 'loadCategoryData',
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.overlay {
  background-color: $color__content--background__secondary;
  padding: $default-margin-padding-width;
  border: 0.063rem solid $color__fst__gray__030;
  box-shadow: 0 0.1875rem 0.375rem $color__fst__gray__29;
  max-height: calc(100vh - 13rem);
  overflow-y: auto;

  .close-search-overlay {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .close.icon--svg {
      font-size: 1.5rem;
      cursor: pointer;
      width: 2rem;
      height: 2rem;
    }

    h4 {
      margin: 0;
      padding-top: .4rem;
    }
  }

  @include breakpoint-up($lg) {
    width: 64rem;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1rem;
    font-weight: $base-text-font-weight-bold;
    color: $color__fst__primary;
    margin-bottom: .5rem;
    margin-top: 1.5rem;

    .show-all-results {
      display: flex;
      align-items: center;
      gap: .25rem;
      cursor: pointer;
      color: $color__fst__cyan__100;
      font-size: .875rem;
      font-weight: 600;

      .forward-icon {
        display: inline-block;
        width: 1.25rem;
        height: 1.25rem;
        flex-shrink: 0;
        color: $color__fst__cyan__100;
      }
    }
  }

  .h5 {
    margin-bottom: .5rem;
  }

  &__skeleton {
    width: 100%;
  }

  .suggestions-grid-wrapper {
    margin-bottom: 1.5rem;
  }
}
</style>
